//@ts-ignore
import { v4 as uuid } from 'uuid';
import {
  Paper,
  Title,
  Divider,
  Stack,
  Flex,
  Text,
  Image,
  Select,
  ColorInput,
  Group,
  ActionIcon,
  Button,
  useMantineTheme,
  Tooltip,
  MediaQuery,
  FileButton,
  Popover,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { UseListStateHandlers } from '@mantine/hooks';
import {
  getEditorDefaults,
  colorStringToColorArray,
  createDefaultImageWriter,
  createDefaultImageReader,
  blobToFile,
  openDefaultEditor,
  PinturaDefaultImageWriterResult,
} from '@pqina/pintura';
import {
  IconUpload,
  IconX,
  IconPhoto,
  IconTrash,
  IconPlus,
  IconStar,
  IconCamera,
} from '@tabler/icons';
import { AnimatePresence, motion, MotionStyle } from 'framer-motion';
import { forwardRef, MutableRefObject, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import useFitText from 'use-fit-text';
import { isMobile } from 'react-device-detect';
import useStyles from './styles';
import { getSizeForTierName } from '../../src/Utils';
import GradientPicker, {
  TierPresets,
  TIER_GRADIENTS,
  TIER_COLOR_SWATCH,
  UploadImageData,
  TierData,
} from '../GradientPicker/GradientPicker';
import { useStore } from '../../stores/stores';
import FloatingLabelInput from '../FloatingInput/FloatingLabelInput';

export const SelectItem = forwardRef<HTMLDivElement>(({ label, ...others }: any, ref) => (
  <div ref={ref} {...others}>
    <Group sx={{ gap: 3 }}>
      <Text>{label}</Text>
      {label !== 'Default' && <IconStar color="gold" size={15} />}
    </Group>
  </div>
));

export const TierLabel = ({
  tier,
  fitText,
  squareSize,
  bold,
  italics,
  underline,
  styles,
}: {
  tier: TierData;
  squareSize?: number | string;
  fitText?: {
    fontSize: string;
    ref: MutableRefObject<HTMLDivElement>;
  };
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  styles?: MotionStyle | undefined;
}) => {
  const { fontSize, ref } = useFitText({
    maxFontSize: 400,
    logLevel: 'debug',
    resolution: 10,
    minFontSize: 40,
  });
  const theme = useMantineTheme();

  return (
    <motion.div
      ref={fitText ? fitText.ref : ref}
      style={{
        fontSize: fitText ? fitText.fontSize : fontSize,
        fontStyle: italics ? 'italic' : 'normal',
        fontWeight: bold ? 'bold' : 'normal',
        textDecorationLine: underline ? 'underline' : 'none',
        height: squareSize ?? 100,
        width: squareSize ?? 100,
        overflow: 'hidden',
        color: tier.textColor,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
        borderRadius: theme.radius.md,
        textAlign: 'center',
        aspectRatio: '1',
        ...styles,
      }}
      animate={{
        background: tier.gradient,
        backgroundColor: tier.backgroundColor,
      }}
    >
      {tier.name}{' '}
    </motion.div>
    // <motion.div
    //   animate={{
    //     background: tier.gradient,
    //     backgroundColor: tier.backgroundColor,
    //   }}
    //   style={{
    //     width: 100,
    //     height: 100,
    //     overflow: 'hidden',
    //     display: 'flex',
    //     justifyContent: 'center',
    //     alignItems: 'center',
    //     padding: theme.spacing.sm,
    //     borderRadius: theme.radius.md,
    //   }}
    //   ref={ref}
    // >
    //   <motion.div
    //     layout="position"
    //     transition={{
    //       duration: 0.24,
    //     }}
    //   >
    //     <Text
    //       sx={{
    //         verticalAlign: 'center',
    //         fontSize,
    //       }}
    //       w="100%"
    //       align="center"
    //       color={tier.textColor}
    //       lineClamp={6}
    //     >
    //       {' '}
    //       {tier.name}
    //     </Text>
    //   </motion.div>
    // </motion.div>
  );
};
const SetupTiersScreen = observer(
  ({
    setupError,
    setSetupError,
  }: {
    setupError: string;
    setSetupError: (error: string) => void;
  }) => {
    const theme = useMantineTheme();
    const { classes } = useStyles();

    const {
      createStore: { tiersData, setTierItemProp, removeTierDataIdx, appendTierData },
      hasPremium,
      setPaywallOpen,
    } = useStore(null);
    return (
      <>
        <Paper>
          <MediaQuery smallerThan="md" styles={{ display: 'none' }}>
            <Title size={40} weight={900}>
              Setup your Tiers ❤️
            </Title>
          </MediaQuery>
          <MediaQuery largerThan="md" styles={{ display: 'none' }}>
            <Title size={25} weight={800} align="center">
              Setup your Tiers ❤️
            </Title>
          </MediaQuery>
          <Text color="dimmed"> You&apos;ll be able to change them later </Text>
          <Divider my="xs" />
          <Stack>
            <AnimatePresence>
              {tiersData.map((tier, idx) => (
                <motion.div
                  key={tier.id}
                  layout
                  initial={{ opacity: 0, y: 200 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 200, speed: 5 }}
                >
                  {/* DESKTOP VERSION */}
                  <MediaQuery smallerThan="md" styles={{ display: 'none' }}>
                    <Flex align="center" h={135}>
                      {tier.preset === TierPresets.IMAGE && tier.image?.pintura ? (
                        <Image
                          width={100}
                          height={100}
                          radius="md"
                          fit="contain"
                          src={tier.image.pintura.url}
                        />
                      ) : (
                        <TierLabel tier={tier} />
                      )}
                      <Divider size="md" orientation="vertical" mx="xl" />
                      <Stack
                        h="100%"
                        justify="flex-start"
                        sx={{
                          gap: 1,
                        }}
                      >
                        <AnimatePresence>
                          {tier.preset !== TierPresets.IMAGE && (
                            <motion.div
                              key="tierNameHolder"
                              initial={{ opacity: 0, y: -40 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -40 }}
                              transition={{
                                type: 'easeIn',
                              }}
                              layout
                            >
                              <FloatingLabelInput
                                textProps={{
                                  placeholder: 'S,A,B,C,D ...',
                                  label: 'Tier Name',
                                  withAsterisk: false,
                                  sx: {
                                    label: {
                                      fontSize: theme.fontSizes.sm,
                                      color: theme.colors.dark[0],
                                    },
                                  },
                                }}
                                value={tier.name}
                                setValue={(newVal) => {
                                  setSetupError('');
                                  setTierItemProp(idx, 'name', newVal);
                                }}
                              />
                            </motion.div>
                          )}
                          <motion.div layout>
                            <Select
                              itemComponent={SelectItem}
                              label="Tier Preset"
                              placeholder="Default"
                              value={tier.preset}
                              onChange={(newVal) => {
                                if (newVal !== TierPresets.DEFAULT) {
                                  if (!hasPremium) {
                                    setPaywallOpen(true, 'customTierLabel');
                                    return;
                                  }
                                }
                                setTierItemProp(idx, 'preset', newVal as TierPresets);
                                if (newVal === TierPresets.GRADIENT) {
                                  if (!tier.gradient) {
                                    setTierItemProp(idx, 'gradient', TIER_GRADIENTS[0]);
                                  }
                                } else {
                                  setTierItemProp(idx, 'gradient', undefined);
                                }
                                setSetupError('');
                              }}
                              data={[
                                { value: TierPresets.DEFAULT, label: 'Default' },
                                { value: TierPresets.GRADIENT, label: 'Gradient' },
                                { value: TierPresets.IMAGE, label: 'Image' },
                              ]}
                            />
                          </motion.div>
                        </AnimatePresence>
                      </Stack>
                      <Stack h="100%" mx="xl" justify="flex-start">
                        <AnimatePresence mode="wait">
                          {tier.preset === TierPresets.DEFAULT && (
                            <motion.div
                              key="default"
                              initial={{ x: 200 }}
                              animate={{ x: 0 }}
                              exit={{ x: 200, opacity: 0 }}
                              layout
                            >
                              <ColorInput
                                sx={{
                                  input: {
                                    color: 'rgb(255, 255, 255,0)',
                                  },
                                }}
                                shadow="md"
                                placeholder="Pick color"
                                label="Tier Color"
                                disallowInput
                                withEyeDropper={false}
                                withPicker={false}
                                swatches={TIER_COLOR_SWATCH}
                                value={tier.backgroundColor}
                                swatchesPerRow={5}
                                onChange={(color) => {
                                  setTierItemProp(idx, 'backgroundColor', color);
                                }}
                              />
                            </motion.div>
                          )}
                          {tier.preset === TierPresets.GRADIENT && (
                            <motion.div
                              key="gradient"
                              initial={{ x: 200 }}
                              animate={{ x: 0 }}
                              exit={{ x: 200, opacity: 0 }}
                              layout
                            >
                              <GradientPicker
                                selected={tier.gradient}
                                setSelected={(val) => {
                                  setTierItemProp(idx, 'gradient', val);
                                }}
                              />
                            </motion.div>
                          )}
                          {tier.preset === TierPresets.IMAGE && (
                            <motion.div
                              key="imagePicker"
                              initial={{ x: 200, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              exit={{ x: 200, opacity: 0 }}
                              layout
                            >
                              <Dropzone
                                w={350}
                                h={130}
                                multiple={false}
                                onDrop={async (files) => {
                                  const file = files[0];
                                  const imageData: UploadImageData = {
                                    src: file,
                                    id: uuid(),
                                    name: tier.name,
                                    updating: false,
                                  };
                                  const editorConstraints = getEditorDefaults({
                                    imageCropAspectRatio: 1,
                                    imageCropLimitToImage: true,
                                    imageBackgroundColor:
                                      colorStringToColorArray('rgba(255,255,255,0)'),
                                    imageWriter: createDefaultImageWriter({
                                      copyImageHead: false,
                                      targetSize: {
                                        width: 500,
                                        height: 500,
                                        fit: 'contain',
                                        upscale: false,
                                      },
                                      mimeType: 'image/webp',
                                    }),
                                    imageReader: createDefaultImageReader({
                                      preprocessImageFile: async (
                                        testFile,
                                        options,
                                        onprogress
                                      ) => {
                                        // If is not of type HEIC we skip the file
                                        if (!/heic/.test(testFile.type)) return testFile;
                                        const heic2any = (await import('heic2any')).default;
                                        // Let's turn the HEIC image into JPEG so the browser can read it
                                        const blob = await heic2any({
                                          blob: testFile,
                                          toType: 'image/jpeg',
                                          quality: 0.8,
                                        });
                                        // The editor expects a File so let's convert our Blob
                                        return blobToFile(blob as Blob, testFile.name);
                                      },
                                    }),
                                  });
                                  const editor = openDefaultEditor({
                                    src: imageData.src,
                                    class:
                                      theme.colorScheme === 'dark'
                                        ? classes.pinturaDark
                                        : 'pintura-light',
                                    ...editorConstraints,
                                  });
                                  editor.on('close', () => {});
                                  editor.on('processerror', (e) => {
                                    console.log(e);
                                  });
                                  editor.on('process', (data: PinturaDefaultImageWriterResult) => {
                                    imageData.pintura = {
                                      imageState: data.imageState,
                                      dest: data.dest,
                                      url: URL.createObjectURL(data.dest),
                                    };
                                    console.log(imageData);
                                    setTierItemProp(idx, 'image', imageData);
                                  });
                                }}
                                onReject={(files) => console.log('rejected files', files)}
                                maxSize={5 * 1024 ** 2}
                                accept={{
                                  'image/*': ['.png', '.heic', '.jpeg', '.jpg'],
                                }}
                              >
                                <Group position="center" style={{ pointerEvents: 'none' }}>
                                  <Dropzone.Accept>
                                    <IconUpload
                                      size="3.2rem"
                                      stroke={1.5}
                                      color={
                                        theme.colors[theme.primaryColor][
                                          theme.colorScheme === 'dark' ? 4 : 6
                                        ]
                                      }
                                    />
                                  </Dropzone.Accept>
                                  <Dropzone.Reject>
                                    <IconX
                                      size="3.2rem"
                                      stroke={1.5}
                                      color={theme.colors.red[theme.colorScheme === 'dark' ? 4 : 6]}
                                    />
                                  </Dropzone.Reject>
                                  <Dropzone.Idle>
                                    <IconPhoto size="2.4rem" stroke={1.5} />
                                  </Dropzone.Idle>
                                  <div>
                                    <Text size="lg" inline>
                                      Drag image here or click to select a file
                                    </Text>
                                  </div>
                                </Group>
                              </Dropzone>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Stack>
                      <ActionIcon
                        onClick={() => {
                          removeTierDataIdx(idx);
                        }}
                      >
                        <IconTrash color="red" />
                      </ActionIcon>
                    </Flex>
                  </MediaQuery>
                  {/* MOBILE VERSION */}
                  <MediaQuery largerThan="md" styles={{ display: 'none' }}>
                    <Flex align="center" py="xs" style={{ justifyContent: 'center' }}>
                      {tier.preset === TierPresets.IMAGE && tier.image?.pintura ? (
                        <Image
                          width={75}
                          height={75}
                          radius="md"
                          fit="contain"
                          src={tier.image.pintura.url}
                        />
                      ) : (
                        <Group>
                          <TierLabel styles={{ padding: 0 }} squareSize={75} tier={tier} />
                        </Group>
                      )}
                      <Divider size="md" orientation="vertical" mx="sm" />
                      <Stack
                        h="100%"
                        w="100%"
                        justify="flex-start"
                        sx={{
                          gap: 1,
                        }}
                      >
                        <AnimatePresence>
                          {tier.preset !== TierPresets.IMAGE && (
                            <motion.div
                              key="tierNameHolder"
                              initial={{ opacity: 0, y: -40 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -40 }}
                              transition={{
                                type: 'easeIn',
                              }}
                              layout
                            >
                              <FloatingLabelInput
                                textProps={{
                                  placeholder: 'S,A,B,C,D ...',
                                  label: 'Tier Name',
                                  withAsterisk: false,
                                  sx: {
                                    fontSize: 16,
                                    label: {
                                      fontSize: theme.fontSizes.sm,
                                      color: theme.colors.dark[0],
                                    },
                                  },
                                }}
                                value={tier.name}
                                setValue={(newVal) => {
                                  setSetupError('');
                                  setTierItemProp(idx, 'name', newVal);
                                }}
                              />
                            </motion.div>
                          )}
                          <motion.div layout>
                            <Select
                              itemComponent={SelectItem}
                              label="Tier Preset"
                              placeholder="Default"
                              value={tier.preset}
                              onChange={(newVal) => {
                                if (newVal !== TierPresets.DEFAULT) {
                                  if (!hasPremium) {
                                    setPaywallOpen(true, 'customTierLabel');
                                    return;
                                  }
                                }
                                setTierItemProp(idx, 'preset', newVal as TierPresets);
                                if (newVal === TierPresets.GRADIENT) {
                                  if (!tier.gradient) {
                                    setTierItemProp(idx, 'gradient', TIER_GRADIENTS[0]);
                                  }
                                } else {
                                  setTierItemProp(idx, 'gradient', undefined);
                                }
                                setSetupError('');
                              }}
                              data={[
                                { value: TierPresets.DEFAULT, label: 'Default' },
                                { value: TierPresets.GRADIENT, label: 'Gradient' },
                                { value: TierPresets.IMAGE, label: 'Image' },
                              ]}
                            />
                          </motion.div>
                        </AnimatePresence>
                        <AnimatePresence mode="wait">
                          {tier.preset === TierPresets.DEFAULT && (
                            <motion.div
                              key="default"
                              initial={{ x: 200 }}
                              animate={{ x: 0 }}
                              exit={{ x: 200, opacity: 0 }}
                              layout
                            >
                              <ColorInput
                                sx={{
                                  input: {
                                    color: 'rgb(255, 255, 255,0)',
                                  },
                                }}
                                shadow="md"
                                placeholder="Pick color"
                                label="Tier Color"
                                disallowInput
                                withEyeDropper={false}
                                withPicker={false}
                                swatches={TIER_COLOR_SWATCH}
                                value={tier.backgroundColor}
                                swatchesPerRow={5}
                                onChange={(color) => {
                                  setTierItemProp(idx, 'backgroundColor', color);
                                }}
                              />
                            </motion.div>
                          )}

                          {tier.preset === TierPresets.GRADIENT && (
                            <motion.div
                              key="gradient"
                              initial={{ x: 200 }}
                              animate={{ x: 0 }}
                              exit={{ x: 200, opacity: 0 }}
                              layout
                            >
                              <Text color="#C1C2C5" fw={450} fz="0.875rem">
                                Tier Gradient
                              </Text>
                              <Popover
                                width={300}
                                trapFocus
                                position="bottom"
                                withArrow
                                shadow="md"
                              >
                                <Popover.Target>
                                  <Button
                                    sx={{
                                      width: '100%',
                                      borderRadius: 8,
                                      height: 25,
                                    }}
                                    variant="gradient"
                                    bg={tier.gradient}
                                  />
                                </Popover.Target>
                                <Popover.Dropdown
                                  sx={{
                                    background:
                                      theme.colorScheme === 'dark'
                                        ? theme.colors.dark[7]
                                        : theme.white,
                                  }}
                                >
                                  <GradientPicker
                                    selected={tier.gradient}
                                    setSelected={(val) => {
                                      setTierItemProp(idx, 'gradient', val);
                                    }}
                                  />
                                </Popover.Dropdown>
                              </Popover>
                            </motion.div>
                          )}
                          {tier.preset === TierPresets.IMAGE && (
                            <motion.div
                              key="imagePicker"
                              initial={{ x: 200, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              exit={{ x: 200, opacity: 0 }}
                              layout
                            >
                              <FileButton
                                accept={'image/*'}
                                onChange={(file) => {
                                  if (!file) {
                                    console.log('No file was attached');
                                    return;
                                  }
                                  const imageData: UploadImageData = {
                                    src: file,
                                    id: uuid(),
                                    name: tier.name,
                                    updating: false,
                                  };
                                  const editorConstraints = getEditorDefaults({
                                    imageCropAspectRatio: 1,
                                    imageCropLimitToImage: true,
                                    imageBackgroundColor:
                                      colorStringToColorArray('rgba(255,255,255,0)'),
                                    imageWriter: createDefaultImageWriter({
                                      copyImageHead: false,
                                      targetSize: {
                                        width: 500,
                                        height: 500,
                                        fit: 'contain',
                                        upscale: false,
                                      },
                                      mimeType: 'image/webp',
                                    }),
                                    imageReader: createDefaultImageReader({
                                      preprocessImageFile: async (
                                        testFile,
                                        options,
                                        onprogress
                                      ) => {
                                        // If is not of type HEIC we skip the file
                                        if (!/heic/.test(testFile.type)) return testFile;
                                        const heic2any = (await import('heic2any')).default;
                                        // Let's turn the HEIC image into JPEG so the browser can read it
                                        const blob = await heic2any({
                                          blob: testFile,
                                          toType: 'image/jpeg',
                                          quality: 0.8,
                                        });
                                        // The editor expects a File so let's convert our Blob
                                        return blobToFile(blob as Blob, testFile.name);
                                      },
                                    }),
                                  });
                                  const editor = openDefaultEditor({
                                    src: imageData.src,
                                    class:
                                      theme.colorScheme === 'dark'
                                        ? classes.pinturaDark
                                        : 'pintura-light',
                                    ...editorConstraints,
                                  });
                                  editor.on('close', () => {});
                                  editor.on('processerror', (e) => {
                                    console.log(e);
                                  });
                                  editor.on('process', (data: PinturaDefaultImageWriterResult) => {
                                    imageData.pintura = {
                                      imageState: data.imageState,
                                      dest: data.dest,
                                      url: URL.createObjectURL(data.dest),
                                    };
                                    console.log(imageData);
                                    setTierItemProp(idx, 'image', imageData);
                                  });
                                }}
                              >
                                {(props) => (
                                  <Button
                                    {...props}
                                    px={10}
                                    py={5}
                                    w="100%"
                                    sx={{
                                      marginTop: 8,
                                      borderWidth: 0.5,
                                      border: 'solid',
                                      borderRadius: 4,
                                      borderColor: theme.colors.dark[4],
                                      color: theme.colors.dark[1],
                                      backgroundColor: theme.colors.dark[6],
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <IconCamera style={{ marginRight: 8 }} />
                                    <Text>Upload Camera</Text>
                                  </Button>
                                )}
                              </FileButton>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Stack>
                      <ActionIcon
                        onClick={() => {
                          removeTierDataIdx(idx);
                        }}
                        ml="xl"
                      >
                        <IconTrash color="red" />
                      </ActionIcon>
                    </Flex>
                  </MediaQuery>
                </motion.div>
              ))}
              <motion.div
                key="AddButton"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                layout
              >
                <Button
                  onClick={() => {
                    let newColor =
                      TIER_COLOR_SWATCH[Math.floor(Math.random() * TIER_COLOR_SWATCH.length)];
                    let tries = 1;
                    const isMatch = (tier: TierData) => tier.backgroundColor === newColor;
                    while (
                      tries < TIER_COLOR_SWATCH.length * 2 &&
                      tiersData.filter(isMatch).length > 0
                    ) {
                      tries += 1;
                      newColor =
                        TIER_COLOR_SWATCH[Math.floor(Math.random() * TIER_COLOR_SWATCH.length)];
                    }

                    const newTiers: TierData = {
                      name:
                        tiersData.length === 0
                          ? 'S'
                          : String.fromCharCode('A'.charCodeAt(0) + tiersData.length - 1),
                      backgroundColor: newColor,
                      preset: TierPresets.DEFAULT,
                      textColor: 'white',
                      id: uuid(),
                    };

                    appendTierData(newTiers);
                    setSetupError('');
                  }}
                  color="pink"
                  leftIcon={<IconPlus />}
                >
                  Add a Tier
                </Button>
              </motion.div>
            </AnimatePresence>
          </Stack>
          {setupError && <Text color="red"> {setupError} </Text>}
        </Paper>
      </>
    );
  }
);

export default SetupTiersScreen;
